// Pieces the performance views share (plan step 3.4, Console D): the app in scope, the release a
// view compares with the one before it, the page head and its meta line, a signed change, and the
// histogram. The pages themselves come from the sidebar's tree (components/shell/Sidebar.tsx); the
// release and the window are the filter bar's (APS-195). Charts follow docs/brand.md: ink on paper,
// axes with units, no hue per series.
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import {
  appsQuery,
  type DeviceParams,
  lastDays,
  type PerfScope,
  releasesQuery,
} from '../api/queries'
import type {
  App,
  FineHistogram,
  Histogram as HistogramData,
  IssueSummary,
  ReleaseHealth,
} from '../api/types'
import { type Translator, useI18n } from '../i18n'
import { compact, duration } from '../lib/format'
import { fitFine, niceCountTop } from '../lib/histogram'
import { issueTitle } from '../lib/issues'
import { type Latency, latency } from '../lib/latency'
import { releasesNewestFirst } from '../lib/releases'
import { useProjectParams } from '../routes/params'
import { carried, deviceParams, type PerfSearch, rangeDays } from '../routes/search'
import { Bars, EmptyNote, HovRow, Kind, PageHead, Spark, Status, Table } from './d'
import { appLabel } from './Shell'

/** The scope of a performance view: the URL's app (or the project's first), release, days. */
export function usePerfScope(): {
  scope: PerfScope
  app: App | undefined
  apps: App[]
  appsPending: boolean
  search: PerfSearch
} {
  const { orgId, projectId } = useProjectParams()
  const search = useSearch({ strict: false }) as PerfSearch
  const apps = useQuery(appsQuery(orgId, projectId))
  const list = apps.data ?? []
  const app = list.find((a) => a.id === search.app) ?? (search.app ? undefined : list[0])
  return {
    scope: {
      app_id: app?.id ?? search.app,
      release: search.release,
      days: rangeDays(search.range, 'perf'),
      ...deviceParams(search),
    },
    app,
    apps: list,
    appsPending: apps.isPending,
    search,
  }
}

/** How far back a view looks for the release before the one it compares (D103 #14). */
const RELEASE_LOOKBACK_DAYS = 90

/**
 * The releases a performance view compares (D103 #14): the selected release, or the latest
 * when every release is in scope, and the one before it. Deltas read "vs <previous>"; with
 * every release in scope, the meta says the latest is compared with the previous.
 */
export function usePerfReleases(scope: PerfScope): {
  list: ReleaseHealth[]
  current: string | undefined
  previous: string | undefined
} {
  const { projectId } = useProjectParams()
  const range = lastDays(RELEASE_LOOKBACK_DAYS)
  const releases = useQuery({
    ...releasesQuery(projectId, { ...range, app_id: scope.app_id }, [
      'perf-releases',
      scope.app_id,
      RELEASE_LOOKBACK_DAYS,
    ]),
    enabled: !!scope.app_id,
  })
  const list = releasesNewestFirst(releases.data ?? [], scope.app_id)
  const current = scope.release ?? list[0]?.release
  const at = list.findIndex((r) => r.release === current)
  return { list, current, previous: at >= 0 ? list[at + 1]?.release : undefined }
}

/**
 * The dashed marker where the compared release shipped, over a daily chart: the index of the
 * day it was first seen, when that day is in the chart.
 */
export function releaseMarker(
  list: ReleaseHealth[],
  release: string | undefined,
  days: readonly string[],
): { index: number; label: string } | undefined {
  const first = list.find((r) => r.release === release)?.released_at.slice(0, 10)
  const index = first ? days.indexOf(first) : -1
  return index > 0 && release ? { index, label: release } : undefined
}

/** A scope with one release in it: the compared release's twin of a query. */
export const withRelease = (scope: PerfScope, release: string | undefined): PerfScope => ({
  ...scope,
  release,
})

/**
 * The mono meta line under a performance title: the app, the release (or every release) and the
 * window, then what the view adds (its count, its signal, its definition).
 */
/**
 * The meta's part for the bar's device filters (APS-228), so a screenshot says what it shows:
 * `iPhone17,1 · iOS 26.1` where the page applies them, or that it does not where it does not
 * (`na`); nothing when none is set. The OS reads with its app's OS name when one app is in scope.
 */
export function deviceMeta(
  i18n: Translator,
  app: App | undefined,
  d: DeviceParams,
  mode: 'apply' | 'na' = 'apply',
): string | undefined {
  if (!d.device_model && !d.os_version) return undefined
  if (mode === 'na')
    return i18n.t(
      d.device_model && d.os_version
        ? 'filters.both.notApplied'
        : d.device_model
          ? 'filters.device.notApplied'
          : 'filters.os.notApplied',
    )
  const os = app?.platform === 'ios' ? 'iOS' : app?.platform === 'android' ? 'Android' : undefined
  return [d.device_model, d.os_version && [os, d.os_version].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(' · ')
}

export function perfMeta(
  i18n: Translator,
  app: App | undefined,
  scope: PerfScope,
  more: (string | false | null | undefined)[] = [],
  devices: 'apply' | 'na' = 'apply',
): string {
  const { t } = i18n
  return [
    app ? appLabel(app) : undefined,
    scope.release ?? t('perf.meta.allReleases'),
    devices === 'apply' ? deviceMeta(i18n, app, scope) : undefined,
    t('perf.meta.days', { days: scope.days }),
    ...more,
    devices === 'na' ? deviceMeta(i18n, app, scope, 'na') : undefined,
  ]
    .filter((x): x is string => !!x)
    .join(' · ')
}

/** The meta's part naming what the deltas compare (D103 #14), or nothing without a previous. */
export function deltaMeta(
  t: Translator['t'],
  scope: PerfScope,
  current: string | undefined,
  previous: string | undefined,
): string | null {
  if (!previous || !current) return null
  return scope.release
    ? t('perf.meta.compareTo', { previous })
    : t('perf.meta.compare', { current, previous })
}

/** The page head of a performance view: "Performance / <page>" over every page but the overview. */
export function PerfHead({
  title,
  page,
  meta,
  actions,
  mono,
}: {
  title: ReactNode
  /** The page's name in the crumb (or its parent's link, then more); none on the overview. */
  page?: ReactNode
  meta?: ReactNode
  actions?: ReactNode
  /** A detail page: the title is the object's own name (a screen, an endpoint, a trace id). */
  mono?: boolean
}) {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const search = useSearch({ strict: false }) as PerfSearch
  return (
    <PageHead
      crumb={
        page ? (
          <>
            <Link
              to="/o/$orgId/p/$projectId/performance"
              params={{ orgId, projectId }}
              search={carried(search)}
            >
              {t('nav.performance')}
            </Link>
            {' / '}
            {page}
          </>
        ) : undefined
      }
      title={title}
      variant={mono ? 'mono' : undefined}
      meta={meta ? <span translate="no">{meta}</span> : undefined}
      actions={actions}
    />
  )
}

/** A release, linking to the releases view scoped to it. */
export function ReleaseLink({ release }: { release: string }) {
  const { orgId, projectId } = useProjectParams()
  const search = useSearch({ strict: false }) as PerfSearch
  return (
    <Link
      to="/o/$orgId/p/$projectId/releases/$release"
      params={{ orgId, projectId, release }}
      search={{ ...carried(search), release: undefined }}
      translate="no"
    >
      {release || '—'}
    </Link>
  )
}

/** What to show when the project has no app yet. */
export function NoApp() {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  return (
    <EmptyNote title={t('perf.noApp')}>
      <Link to="/o/$orgId/p/$projectId/setup" params={{ orgId, projectId }}>
        {t('releases.empty.action')}
      </Link>
    </EmptyNote>
  )
}

/**
 * A signed change against the previous release: lower is better for every performance metric,
 * so a fall reads ok and a rise warn (`bad` makes a rise the crash colour, for failures). "—"
 * when either side is missing, and "no change" when it rounds to nothing, as the release
 * comparison says it (APS-409).
 */
export function Delta({
  value,
  format,
  bad,
}: {
  value: number | null | undefined
  format: (v: number) => string
  bad?: boolean
}) {
  const { t } = useI18n()
  if (value === null || value === undefined || !Number.isFinite(value))
    return <span className="faint">—</span>
  const shown = format(Math.abs(value))
  if (shown === format(0)) return <span className="faint">{t('releases.change.none')}</span>
  return (
    <span className={value < 0 ? 'c-ok' : bad ? 'c-crash' : 'c-warn'}>
      {value < 0 ? '−' : '+'}
      {shown}
    </span>
  )
}

/** A latency in a cell; an approximate one says where it comes from on hover. */
export function LatencyText({ v }: { v: Latency | null | undefined }) {
  const i18n = useI18n()
  return v?.approx ? (
    <span className="approx" title={i18n.t('perf.approx.title')}>
      {latency(i18n, v)}
    </span>
  ) : (
    latency(i18n, v)
  )
}

/**
 * What the devices counted beside what the released spans sampled (D114, APS-445), for a strip
 * line: short, with the whole sentence on hover.
 */
export function Counted({ exact, estimated }: { exact: number; estimated: number }) {
  const i18n = useI18n()
  const n = { exact: compact(i18n, exact), estimated: compact(i18n, estimated) }
  return (
    <span title={i18n.t('perf.counted', n)}>
      {estimated === 0 ? i18n.t('perf.countedAll') : i18n.t('perf.countedShort', n)}
    </span>
  )
}

/** A strip figure that is approximate: "≈ 240", saying where it comes from on hover. */
export function ApproxFigure({ children }: { children: string }) {
  const { t } = useI18n()
  return (
    <span className="approx" title={t('perf.approx.title')}>
      {`≈ ${children}`}
    </span>
  )
}

/** A bucket's label: up to its bound, or above the last one. */
export function bucketLabel(i18n: Translator, bounds: number[], i: number): string {
  const hi = bounds[i]
  return hi === undefined ? `> ${duration(i18n, bounds.at(-1) ?? 0)}` : `≤ ${duration(i18n, hi)}`
}

/** An axis edge: seconds past a second, the unit only on the last label (the mock's). */
function edge(ms: number, last: boolean, seconds: boolean): string {
  const v = seconds ? `${Number((ms / 1000).toFixed(2))}` : `${ms}`
  return ms === 0 ? '0' : last ? `${v} ${seconds ? 's' : 'ms'}` : v
}

/** Where a value falls on bars over irregular bounds: the bucket's index plus its share of it. */
function position(bounds: number[], v: number): number {
  let lo = 0
  for (let i = 0; i < bounds.length; i++) {
    const hi = bounds[i] as number
    if (v <= hi) return i + (hi > lo ? (v - lo) / (hi - lo) : 0)
    lo = hi
  }
  return bounds.length + 0.5
}

/**
 * A duration histogram on Console D's bars: even buckets (`fine`, APS-304) when the API sends
 * them, the coarse ones otherwise; dashed p50 and p90 markers, the bars past p90 in full ink,
 * and the buckets as a list for screen readers.
 */
export function Histogram({
  data,
  fine,
  p50,
  p90,
  label,
  height,
  ticks,
  fit = false,
  hiFromMs,
  onBucket,
  bucketHint,
  countName,
}: {
  data: HistogramData
  fine?: FineHistogram | undefined
  /** Fit the x-domain to the data, 0 to about 1.5 × p99 (APS-399), instead of the API's range. */
  fit?: boolean | undefined
  /** Darken the bars from here (ms) instead of from p90: the "slower" set of a two-hump split. */
  hiFromMs?: number | undefined
  /** Edges (ms) to label under fine buckets, each with its unit, plus the overflow bar. */
  ticks?: number[] | undefined
  p50?: number | null | undefined
  p90?: number | null | undefined
  label: string
  height?: number | undefined
  /** A click on a bar opens a list from that bar's lower edge (ms), as Screen detail's sessions. */
  onBucket?: ((fromMs: number) => void) | undefined
  /** What that click opens, as the tooltip's last line. */
  bucketHint?: string | undefined
  /** A bar's count with its noun, for the tooltip and the table ("2 requests"). */
  countName?: ((n: number) => string) | undefined
}) {
  const i18n = useI18n()
  const { number, t } = i18n
  const marks = [
    ...(p50 !== null && p50 !== undefined ? [{ v: p50, t: 'p50' }] : []),
    ...(p90 !== null && p90 !== undefined ? [{ v: p90, t: 'p90' }] : []),
  ]
  let values: number[]
  let xLabels: [number, string][]
  let at: (v: number) => number
  let items: { label: string; count: number }[]
  // Each bar's lower edge in ms, for a click through.
  let edges: number[]
  const fitted = fine && fit ? fitFine(fine) : fine
  if (fitted && fitted.counts.length > 0) {
    const fine = fitted
    const b = fine.bucket_ms
    const n = fine.counts.length
    // Even buckets, then one bar for everything past the last (APS-304).
    values = [...fine.counts, fine.overflow]
    const every = n > 32 ? 10 : 5
    const seconds = n * b >= 1000
    xLabels = []
    if (ticks) {
      const short = (v: number) => (v < 1000 ? `${v} ms` : `${Number((v / 1000).toFixed(2))} s`)
      for (const v of ticks) if (v > 0 && v < n * b) xLabels.push([v / b - 0.5, short(v)])
      xLabels.push([n, `>${short(n * b)}`])
    } else
      for (let k = 0; k * every <= n; k++)
        xLabels.push([k * every - 0.5, edge(k * every * b, k * every + every > n, seconds)])
    at = (v) => Math.min(v / b, n)
    items = values.map((c, i) => ({
      label:
        i < n
          ? `${duration(i18n, i * b)} – ${duration(i18n, (i + 1) * b)}`
          : `> ${duration(i18n, n * b)}`,
      count: c,
    }))
    edges = values.map((_, i) => Math.min(i, n) * b)
  } else {
    values = [...data.counts]
    const bounds = data.bounds_ms
    xLabels = values.map((_, i) => [i, bucketLabel(i18n, bounds, i)] as [number, string])
    if (values.length > 6) xLabels = xLabels.filter(([i]) => i % 2 === 0)
    at = (v) => position(bounds, v)
    items = values.map((c, i) => ({ label: bucketLabel(i18n, bounds, i), count: c }))
    edges = values.map((_, i) => (i === 0 ? 0 : (bounds[i - 1] ?? 0)))
  }
  const total = values.reduce((a, b) => a + b, 0)
  // Counts tick at round, whole steps that cover the tallest bar (0 / 1,000 / 2,000), as every
  // count axis reads (Mosaic, APS-399).
  const max = niceCountTop(Math.max(0, ...values))
  const p90At = marks.find((m) => m.t === 'p90')
  return (
    <figure className="pf-hist">
      <Bars
        values={values}
        label={[label, ...marks.map((m) => `${m.t} ${duration(i18n, m.v)}`)].join(', ')}
        height={height ?? 200}
        max={max}
        format={(v) => compact(i18n, v)}
        xLabels={xLabels}
        // Dark from the split's "slower" side when there is one (Mosaic, APS-392), else from p90.
        {...(hiFromMs !== undefined
          ? { hiFrom: Math.ceil(at(hiFromMs)) }
          : p90At
            ? { hiFrom: Math.ceil(at(p90At.v)) }
            : {})}
        // Each line says its value, as the mock's "p50 1.08 s" (APS-382).
        percentiles={marks.map((m) => ({ index: at(m.v), label: `${m.t} ${duration(i18n, m.v)}` }))}
        labels={items.map((x) => x.label)}
        {...(countName ? { tipFormat: (v: number) => countName(Math.round(v)) } : {})}
        xName={t('chart.bucket')}
        {...(onBucket
          ? {
              onPick: (i: number) => onBucket(edges[i] ?? 0),
              pickHint: () => bucketHint,
            }
          : {})}
      />
      <ol className="visually-hidden">
        {items.map((x) => (
          <li key={x.label}>
            {x.label}: {number(Math.round(x.count))}
            {total > 0 ? ` (${i18n.percent(x.count / total, 0)})` : ''}
          </li>
        ))}
      </ol>
    </figure>
  )
}

/** An issue's state as D's glyph and word. */
export function IssueStatus({ issue }: { issue: IssueSummary }) {
  const s = issue.state?.status
  return (
    <Status
      s={
        s === 'resolved'
          ? 'res'
          : s === 'ignored'
            ? 'mute'
            : issue.state?.regressed_release
              ? 'reg'
              : 'on'
      }
    />
  )
}

/** How many days an issue's sparkline covers (APS-298). */
export const SPARK_DAYS = 14

/** Issues as D's table: title over kind and id, state, sessions, users and 14 days. */
export function IssueTable({ label, issues }: { label: string; issues: IssueSummary[] }) {
  const i18n = useI18n()
  const { t } = i18n
  const { orgId, projectId } = useProjectParams()
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as PerfSearch
  const to = (x: IssueSummary) => ({
    to: '/o/$orgId/p/$projectId/issues/$issueId' as const,
    params: { orgId, projectId, issueId: x.id },
    search: carried(search),
  })
  return (
    <Table label={label} className="pf-tbl">
      <thead>
        <tr>
          <th scope="col">{t('perf.col.issue')}</th>
          <th scope="col">{t('perf.col.status')}</th>
          <th scope="col" className="num">
            {t('releases.sessions')}
          </th>
          <th scope="col" className="num">
            {t('perf.col.users')}
          </th>
          <th scope="col" className="num">
            {t('perf.col.days', { days: SPARK_DAYS })}
          </th>
        </tr>
      </thead>
      <tbody>
        {issues.map((x) => (
          <HovRow key={x.id} onOpen={() => navigate(to(x))}>
            <td className="anywhere">
              <Link {...to(x)}>{issueTitle(x)}</Link>
              <div className="pf-sub">
                <Kind k={x.kind} /> · <span translate="no">{x.id.slice(0, 8)}</span>
              </div>
            </td>
            <td>
              <IssueStatus issue={x} />
            </td>
            <td className="num">{compact(i18n, x.sessions)}</td>
            <td className="num">{compact(i18n, x.users)}</td>
            <td className="num">{x.daily ? <Spark values={x.daily.occurrences} /> : null}</td>
          </HovRow>
        ))}
      </tbody>
    </Table>
  )
}
